import { useMutation, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import type { NotificationBulkBody, NotificationCategory, NotificationDto, NotificationListResponse, NotificationSummary } from '@nexops/shared';
import { notifKeys, notificationsApi, ROW_KINDS } from '../api/notifications.api';

/** Per-row transform: return a new row, `null` to remove it, or the same row when it is not affected. */
type RowFn = (row: NotificationDto) => NotificationDto | null;
type Snapshot = Array<[readonly unknown[], unknown]>;

const isRowQuery = (key: readonly unknown[]) => ROW_KINDS.includes(key[1] as (typeof ROW_KINDS)[number]);
const isInfinite = (d: unknown): d is InfiniteData<NotificationListResponse> => Boolean(d && typeof d === 'object' && 'pages' in d);

function mapResponse(res: NotificationListResponse, fn: RowFn): NotificationListResponse {
  const items: NotificationDto[] = [];
  let unreadDelta = 0;
  let removed = 0;
  for (const row of res.items) {
    const next = fn(row);
    if (next === null) removed += 1;
    else items.push(next);
    const wasUnread = !row.read;
    const isUnread = next ? !next.read : false;
    unreadDelta += Number(isUnread) - Number(wasUnread);
  }
  return {
    ...res,
    items,
    total: Math.max(0, res.total - removed),
    unread: Math.max(0, res.unread + unreadDelta),
    counts: { ...res.counts, unread: Math.max(0, res.counts.unread + unreadDelta), all: Math.max(0, res.counts.all - removed) },
  };
}

/** Unread change the transform causes among cached rows (taken from the first cached list that contains any row). */
function unreadDeltaOf(qc: QueryClient, fn: RowFn): number {
  for (const [key, data] of qc.getQueriesData({ queryKey: notifKeys.root })) {
    if (!isRowQuery(key) || !data) continue;
    const res = isInfinite(data) ? data.pages[0] : (data as NotificationListResponse);
    if (!res?.items?.length) continue;
    return mapResponse(res, fn).unread - res.unread;
  }
  return 0;
}

function useOptimistic<V>(mutationFn: (v: V) => Promise<unknown>, makeFn: (v: V) => RowFn, opts?: { summaryZero?: (v: V) => boolean }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onMutate: async (v: V) => {
      await qc.cancelQueries({ queryKey: notifKeys.root });
      const snapshot: Snapshot = qc.getQueriesData({ queryKey: notifKeys.root }).map(([k, d]) => [k, d]);
      const fn = makeFn(v);
      const delta = opts?.summaryZero?.(v) ? null : unreadDeltaOf(qc, fn);
      qc.setQueriesData({ queryKey: notifKeys.root, predicate: (q) => isRowQuery(q.queryKey) }, (data: unknown) => {
        if (isInfinite(data)) return { ...data, pages: data.pages.map((p) => mapResponse(p, fn)) };
        return data && typeof data === 'object' ? mapResponse(data as NotificationListResponse, fn) : data;
      });
      qc.setQueryData<NotificationSummary>(notifKeys.summary, (s) =>
        s ? { ...s, unread: delta === null ? 0 : Math.max(0, s.unread + delta) } : s,
      );
      return { snapshot };
    },
    onError: (_e, _v, ctx) => {
      ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: notifKeys.root }),
  });
}

const patchRow = (id: string, patch: Partial<NotificationDto>): RowFn => (row) => (row.id === id ? { ...row, ...patch } : row);

/** All inbox mutations, each with an optimistic cache update, rollback on error and a refetch on settle. */
export function useNotificationActions() {
  const now = () => new Date().toISOString();

  const read = useOptimistic((id: string) => notificationsApi.read(id), (id) => patchRow(id, { read: true, readAt: now() }));
  const unread = useOptimistic((id: string) => notificationsApi.unread(id), (id) => patchRow(id, { read: false, readAt: null }));
  const pin = useOptimistic(
    ({ id, pinned }: { id: string; pinned: boolean }) => notificationsApi.pin(id, pinned),
    ({ id, pinned }) => patchRow(id, { pinned }),
  );
  const confirm = useOptimistic(
    (id: string) => notificationsApi.confirm(id),
    (id) => patchRow(id, { read: true, readAt: now(), confirmedAt: now() }),
  );
  const remove = useOptimistic(
    (id: string) => notificationsApi.remove(id),
    (id) => (row) => (row.id === id ? null : row),
  );
  const bulk = useOptimistic(
    (body: NotificationBulkBody) => notificationsApi.bulk(body),
    (body) => {
      const ids = new Set(body.ids);
      return (row) => {
        if (!ids.has(row.id)) return row;
        switch (body.action) {
          case 'READ':
            return { ...row, read: true, readAt: row.readAt ?? now() };
          case 'UNREAD':
            return { ...row, read: false, readAt: null };
          case 'PIN':
            return { ...row, pinned: true };
          case 'UNPIN':
            return { ...row, pinned: false };
          case 'DELETE':
            return null;
        }
      };
    },
  );
  const readAll = useOptimistic(
    (category?: NotificationCategory) => notificationsApi.readAll(category),
    (category) => (row) => (!category || row.category === category ? { ...row, read: true, readAt: row.readAt ?? now() } : row),
    { summaryZero: (category) => !category },
  );

  return { read, unread, pin, confirm, remove, bulk, readAll };
}

export type NotificationActions = ReturnType<typeof useNotificationActions>;
